import { useIntl } from 'react-intl';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { isFinite, toNumber } from 'lodash';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  FormFieldRequirementIndicator,
  PageSpinner,
  PasswordField,
} from '@app/components';
import { useValidator } from '@app/hooks';
import { sharedMessages } from '@app/translations';

import { StepsBar } from '../../StepsBar';

const minDigitsForPIN = 6;
const cnBem = createBemBlockBuilder(['authPageWrapper']);

interface EnterNewPinFormProps {
  isLoading: boolean;
  onSubmit: (pin: string) => void;
}

export const EnterNewPinForm: FC<EnterNewPinFormProps> = ({
  isLoading,
  onSubmit,
}) => {
  const { $t } = useIntl();
  const [pinRequirements, setPinRequirements] = useState({
    numbers: false,
    sixDigits: false,
  });
  const { validateRequiredField, validateDigitsField } = useValidator();
  const {
    setError,
    handleSubmit: hookFormHandleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    control,
  } = useForm({ mode: 'onTouched' });

  const validatePinField = (value: string) => {
    const isPinValid = validateDigitsField(value, minDigitsForPIN);

    setPinRequirements({
      numbers: isFinite(toNumber(value)),
      sixDigits: value?.length === minDigitsForPIN,
    });

    return isPinValid
      ? undefined
      : $t({
          id: 'resetPin.enterValidPIN',
          defaultMessage: 'Please enter a valid PIN.',
        });
  };

  const validateConfirmField = (value = '') => {
    const requiredMessage = validateRequiredField(value);

    return requiredMessage
      ? $t({
          id: 'resetPin.confirmNewPin',
          defaultMessage: 'Please confirm your new PIN.',
        })
      : undefined;
  };

  const getField = (type: 'PIN' | 'CONFIRM') => {
    const fieldProps = {
      PIN: {
        label: $t({
          id: 'resetPin.newPin',
          defaultMessage: 'New PIN',
        }),
        name: 'pin',
        validate: validatePinField,
      },
      CONFIRM: {
        label: $t({
          id: 'resetPin.confirmPin',
          defaultMessage: 'Confirm new PIN',
        }),
        name: 'confirmPin',
        validate: validateConfirmField,
      },
    };

    const { validate, label, name } = fieldProps[type];

    return (
      <div className={cnBem('__row')}>
        <PasswordField
          label={label}
          name={name}
          control={control}
          rules={{ validate }}
        />
        {type === 'PIN' && (
          <div className={cnBem('__requirementIndicators')}>
            <FormFieldRequirementIndicator
              message={$t({
                id: 'resetPin.onlyNumbersAllowed',
                defaultMessage: 'Only numbers allowed',
              })}
              isValid={pinRequirements.numbers}
            />
            <FormFieldRequirementIndicator
              message={$t({
                id: 'resetPin.mustBeSixDigits',
                defaultMessage: 'Must be six digits',
              })}
              isValid={pinRequirements.sixDigits}
            />
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = ({
    pin,
    confirmPin,
  }: {
    pin: string;
    confirmPin: string;
  }) => {
    if (pin !== confirmPin) {
      return setError('confirmPin', {
        type: 'custom',
        message: $t({
          id: 'collectPin.pinsDoNotMatch',
          defaultMessage: 'PINs do not match.',
        }),
      });
    }

    onSubmit(pin);

    return undefined;
  };

  return (
    <form
      className={cnBem('__form')}
      onSubmit={hookFormHandleSubmit(handleSubmit)}
    >
      {isLoading && <PageSpinner className={cnBem('__spinner')} />}
      <StepsBar stepsAmount={3} currentStepIndex={1} />
      {getField('PIN')}
      {getField('CONFIRM')}
      <div className={cnBem('__row')}>
        <Button
          className={cnBem('__submitButton')}
          styleType="solid"
          type="submit"
          disabled={!isValid || isSubmitting || !isDirty}
        >
          {$t(sharedMessages.resetPin)}
        </Button>
      </div>
    </form>
  );
};
